import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"

import { cn } from "@/lib/utils"

/**
 * One-of-few choice shown as joined segments (DESIGN.md §5.1): every option is
 * visible and one click away, where a dropdown hides the alternatives behind a
 * second click. A radio group underneath, so arrow keys move the selection and
 * screen readers announce "radio, 2 of 4".
 *
 *   <Segmented value={v} onValueChange={set} options={[{ value, label }]} />
 *
 * For more than about six options use Select instead.
 */
const Segmented = React.forwardRef(({ className, options, itemTestIdPrefix, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    orientation="horizontal"
    // No fixed height: the items size it. Under data-density="compact" that is ~32px;
    // on touch surfaces the App.css 48px button floor applies to the items and the
    // frame grows around them instead of the items spilling out of it.
    // Disabled items already fade via App.css `button:disabled` — fading the root too
    // left read-only values at ~0.24 opacity.
    className={cn(
      "inline-flex items-stretch rounded-md border border-input bg-background p-0.5 gap-0.5",
      className
    )}
    {...props}
  >
    {options.map(option => (
      <RadioGroupPrimitive.Item
        key={option.value}
        value={option.value}
        className={cn(
          "min-h-[1.625rem] min-w-[2.5rem] rounded-sm px-2.5 text-body font-medium text-muted-foreground transition-colors",
          "hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15",
          "data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary",
          "disabled:cursor-not-allowed"
        )}
        data-testid={itemTestIdPrefix ? `${itemTestIdPrefix}-${option.value}` : undefined}
      >
        {option.label ?? option.value}
      </RadioGroupPrimitive.Item>
    ))}
  </RadioGroupPrimitive.Root>
))
Segmented.displayName = "Segmented"

export { Segmented }
